import type { AnswerDistributionItem } from '@/api/gen/_model/answerDistributionItem';
import type { Item } from '@/api/gen/_model/item';
import type { OrderItemRef } from '@/api/gen/_model/orderItemRef';
import { prepareEquationHTML } from '@/shared/features/rich-text';

import {
  indexItemsById,
  type MatchLabelPlaceholders,
  resolveItemLabel,
} from './matchPairDisplay';

/**
 * ORDER(순서 맞추기) 보기를 사용자 화면에 노출할 때 쓰는 표시 규칙 — **단일 정본**.
 * MATCH 의 `matchPairDisplay.ts` 와 같은 자리에 있는 ORDER 짝이다.
 *
 * 순서 자체를 되돌리는 규칙은 `orderItems.ts`(`sortItemsByOrderIds`) 가 갖고,
 * 여기서는 **한 줄을 어떤 내용으로 그리는지**와 **통계 집계**를 담당한다.
 *
 * 표시 컴포넌트는 `OrderAnswerList.tsx` 다. 그쪽은 이 모듈이 만든 `OrderAnswerRow`
 * 만 받고 라벨 해석을 다시 하지 않는다 — 해석이 표면마다 갈리면 같은 문항이
 * 결과 카드에서는 평문, 리포트에서는 수식으로 보이는 식으로 어긋난다.
 */

/**
 * 보기 원본 한 개의 표시 내용. 해석 순서는 빌드/풀이/상세와 동일하게
 * richText → 수식 → text 다.
 */
export const getOrderItemContent = (item: Item) =>
  item.richText ||
  (item.equation ? prepareEquationHTML(item.equation) : item.text);

/** 화면에 그릴 한 줄. `content` 는 이미 해석이 끝난 값이다. */
export interface OrderAnswerRow {
  id: string;
  /**
   * 표시 내용(rich text HTML 또는 평문). 보기 원본 → 서버 라벨 → placeholder
   * 순으로 해석되며, 어떤 경우에도 raw id 가 들어가지 않는다.
   */
  content: string;
  /** 보기에 딸린 이미지. 원본을 찾지 못했으면 없다. */
  imageUrl?: string;
}

/**
 * ORDER 제출 순서의 정본은 `answer`다. 조회 API가 롤링 배포 중인 동안에는
 * 이전 응답의 전용 필드를 읽되, 빈 `answer`도 정상 미제출이므로 폴백하지 않는다.
 */
export const getOrderSubmissionIds = ({
  answer,
  submittedAnswerList,
  submittedOrderIds,
}: {
  answer?: readonly string[];
  submittedAnswerList?: readonly string[];
  submittedOrderIds?: readonly string[];
}) => answer ?? submittedAnswerList ?? submittedOrderIds;

/**
 * 한 줄의 표시 내용을 정한다.
 *
 * 보기 원본(`orderItems`)이 있으면 그것이 진실이다 — 서버 라벨은 수식·서식이
 * 평문으로 눌린 값이라, 원본이 있는데 라벨을 쓰면 수식이 날것으로 보인다.
 * 원본을 찾지 못하는 경우(삭제된 보기)에만 라벨, 그마저 없으면 placeholder 다.
 */
const resolveOrderRow = (
  id: string,
  label: string,
  itemById: Map<string, Item>,
  placeholders: MatchLabelPlaceholders,
): OrderAnswerRow => {
  const item = itemById.get(id);
  const itemContent = item && getOrderItemContent(item);

  return {
    id,
    content:
      itemContent ||
      // 이미지 전용 보기는 선택형처럼 라벨/placeholder 없이 이미지만 보여준다.
      // 원본을 찾지 못했을 때만 서버 라벨과 placeholder로 내용을 복원한다.
      (item?.imageUrl ? '' : label || resolveItemLabel(item, placeholders)),
    imageUrl: item?.imageUrl,
  };
};

const toOrderRows = (
  refs: readonly OrderItemRef[],
  itemById: Map<string, Item>,
  placeholders: MatchLabelPlaceholders,
): OrderAnswerRow[] =>
  refs.map(ref => resolveOrderRow(ref.id, ref.label, itemById, placeholders));

const toOrderRowsFromIds = (
  ids: readonly string[],
  itemById: Map<string, Item>,
  placeholders: MatchLabelPlaceholders,
): OrderAnswerRow[] =>
  ids.map(id => resolveOrderRow(id, '', itemById, placeholders));

/**
 * 순서 한 줄 목록(정답이든 제출이든)을 표시 행으로 만든다.
 *
 * 라벨을 담은 `refs` 가 우선이고, 없으면 raw id 배열을 보기 목록과 조인한다.
 * 두 경로 모두 같은 해석 규칙을 거치므로 표면마다 결과가 갈리지 않는다.
 */
export const resolveOrderRows = ({
  refs,
  ids,
  orderItems,
  placeholders,
}: {
  refs?: readonly OrderItemRef[];
  ids?: readonly string[];
  orderItems?: readonly Item[];
  placeholders: MatchLabelPlaceholders;
}): OrderAnswerRow[] => {
  const itemById = indexItemsById(orderItems);

  if (refs != null && refs.length > 0)
    return toOrderRows(refs, itemById, placeholders);
  if (ids != null && ids.length > 0)
    return toOrderRowsFromIds(ids, itemById, placeholders);

  return [];
};

const findCorrectOrderItems = (
  distribution: readonly AnswerDistributionItem[],
): OrderItemRef[] | undefined =>
  distribution.find(
    row => row.correctOrderItems != null && row.correctOrderItems.length > 0,
  )?.correctOrderItems;

const findCorrectOrderIds = (
  distribution: readonly AnswerDistributionItem[],
): string[] | undefined =>
  distribution.find(
    row => row.correctOrderIds != null && row.correctOrderIds.length > 0,
  )?.correctOrderIds;

/**
 * **정답 순서 행 목록**을 뽑는다.
 *
 * 우선순위는 문항이 준 `correctOrderIds` → 분포 행의 `correctOrderItems`(라벨 포함)
 * → 분포 행의 `correctOrderIds` 다. 문항을 먼저 보는 이유는 **아무도 풀지 않은 문항**
 * 이다 — 분포가 비어 있어도 정답 순서는 보여야 한다(MATCH 의 `matchAnswerPairs` 와
 * 같은 경로). 라벨·이미지·수식은 어느 경로든 `orderItems` 로 보강한다.
 *
 * ⚠️ 정답 순서를 알 수 없으면(공개 정책상 부재) **빈 배열**을 돌려준다.
 * `orderItems` 의 원본 배열 순서는 정답 순서가 아니라서, 그대로 번호를 붙여 그리면
 * 없는 정답을 지어내는 셈이 된다.
 */
export const resolveOrderAnswerRows = ({
  distribution,
  orderItems,
  correctOrderIds,
  placeholders,
}: {
  distribution: readonly AnswerDistributionItem[];
  orderItems?: readonly Item[];
  /** 문항이 직접 내려준 정답 순서. 분포보다 우선한다. */
  correctOrderIds?: readonly string[];
  placeholders: MatchLabelPlaceholders;
}): OrderAnswerRow[] => {
  const itemById = indexItemsById(orderItems);

  if (correctOrderIds != null && correctOrderIds.length > 0) {
    return toOrderRowsFromIds(correctOrderIds, itemById, placeholders);
  }

  const distributionOrderItems = findCorrectOrderItems(distribution);
  if (distributionOrderItems) {
    return toOrderRows(distributionOrderItems, itemById, placeholders);
  }

  const distributionOrderIds = findCorrectOrderIds(distribution);
  if (distributionOrderIds) {
    return toOrderRowsFromIds(distributionOrderIds, itemById, placeholders);
  }

  return [];
};

/** 제출 답안에서 정답과 같은 자리에 놓인 항목의 index 집합. */
export const getMatchedOrderPositions = (
  submittedIds: readonly string[],
  correctOrderIds?: readonly string[],
) => {
  if (correctOrderIds == null || correctOrderIds.length === 0) return undefined;

  return new Set(
    submittedIds.flatMap((id, index) =>
      correctOrderIds[index] === id ? [index] : [],
    ),
  );
};

/**
 * 정답 순서 `index` 번째 자리에 `itemId` 를 놓은 학생 수.
 *
 * 분포 행 하나는 "같은 순서로 제출한 학생 묶음"이라, MATCH 의 pair 단위 집계처럼
 * 행을 그대로 쓸 수 없다. 시안(25495:9481)은 정답 순서 **줄마다** 응답률을 붙이므로
 * 자리별로 다시 센다.
 *
 * 판정은 라벨이 아니라 id 로만 한다 — 같은 문구를 서로 다른 보기로 저장할 수 있어서
 * 텍스트로 비교하면 중복 텍스트 문항에서 조용히 어긋난다.
 *
 * ⚠️ 이 수치는 **부분 점수가 아니다.** 서버 채점은 전체 일치 하나뿐이고, 여기 값은
 * "이 자리를 맞춘 학생 수"라는 자리별 응답률일 뿐이다.
 */
export const countOrderPositionMatches = (
  distribution: readonly AnswerDistributionItem[],
  index: number,
  itemId: string,
) =>
  distribution.reduce(
    (count, row) =>
      getOrderSubmissionIds(row)?.[index] === itemId
        ? count + row.count
        : count,
    0,
  );
